import { useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { BasePage } from '../../../../../components/BaseMain';
import { goToAdmin, goToCreateCompany } from '../../../../../router/coordinator';
import {
  AlertMessage,
  EmptyTable,
  FilterContainer,
  HiddenForPermission,
  Loading,
  TitlePages
} from '../../../../../components';
import { useForm } from '../../../../../hooks/useForm';
import { Button, Grid, MenuItem, TextField } from '@mui/material';
import { MobileTable } from './TablesComponents/MobileTable';
import { DesktopTable } from './TablesComponents/DesktopTable';
import { clearAlertMessage, hasPermission, isDesktop } from '../../../../../utils';
import { useEffect, useState } from 'react';
import { listAccessCompanies, listIndustries } from '../../../../../services';
import { useDebounce } from 'use-debounce';
import { GENERIC_ERROR } from '../../../../../utils/constantes';
import { Table } from './styled';
import { Order } from '../../../../../utils/enums';
import { FilterButton } from '../../../../../components/FilterButton';

export function ListCompaniesPage() {
  const navigate = useNavigate();
  const breadcrumbs = [
    { title: 'Administrativo', functionCoordinator: () => goToAdmin(navigate) },
    { title: 'Empresas' }
  ];

  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [offset, setOffset] = useState(5);
  const [order, setOrder] = useState(Order.DESC);

  const [filters, onChange, clearForm] = useForm({
    name: '',
    document: '',
    industry: ''
  });
  const [debouncedFilter] = useDebounce(filters, 500);

  const [companies, setCompanies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState({
    title: '',
    message: '',
    severity: 'error'
  });

  useEffect(async () => {
    await init();
  }, []);
  useEffect(async () => await list(), [page, offset, order, debouncedFilter]);

  const handlePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setOffset(parseInt(event.target.value, 10));
    setPage(0);
  };

  const init = async () => {
    try {
      const result = await Promise.all([
        listAccessCompanies(page, offset, order, filters),
        listIndustries()
      ]).then((res) => ({
        companies: res[0],
        industries: res[1]
      }));

      setCompanies(result.companies.data);
      setCount(result.companies.count);

      setIndustries(result.industries);
      setLoading(false);
      setIsFirstTime(false);
    } catch (err) {
      setAlertMessage({
        title: err.message || GENERIC_ERROR,
        message: err.trace && `Código: ${err.trace}`
      });
      setLoading(false);
      setIsFirstTime(false);
    }
  };

  const list = async () => {
    try {
      if (isFirstTime) {
        return;
      }
      setLoading(true);

      const filter = filters;
      const result = await listAccessCompanies(page, offset, order, filter);
      setCompanies(result.data);
      setCount(result.count);

      setLoading(false);
    } catch (error) {
      setAlertMessage({ title: error.message || GENERIC_ERROR });
      setLoading(false);
    }
  };

  const handleOrder = () => {
    if (order === Order.ASC) {
      setOrder(Order.DESC);
      return;
    }

    setOrder(Order.ASC);
  };

  function TableResponsive() {
    if (!companies?.length && !loading) {
      return <EmptyTable />;
    }

    if (isDesktop()) {
      return (
        <DesktopTable
          companies={companies}
          loading={loading}
          navigate={navigate}
          count={count}
          page={page}
          offset={offset}
          order={order}
          handlePage={handlePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleOrder={handleOrder}
        />
      );
    }

    return (
      <MobileTable
        companies={companies}
        navigate={navigate}
        loading={loading}
        count={count}
        page={page}
        offset={offset}
        handlePage={handlePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    );
  }

  return (
    <BasePage navigate={navigate} breadcrumbs={breadcrumbs}>
      <TitlePages
        title="Lista de empresas de acesso"
        description="Lista de todas as empresas cadastradas do grupo graça."
        textButton="Cadastrar empresa"
        Icon={AddCircleOutlineIcon}
        onClickButton={() => goToCreateCompany(navigate)}
        keyPermissionButton="CREATE_COMPANY"
        toHideDivider={!hasPermission('LIST_ACCESS_COMPANY')}
      />

      <HiddenForPermission keys="LIST_ACCESS_COMPANY">
        <Grid container>
          <AlertMessage
            title={alertMessage.title}
            message={alertMessage.message}
            severity={alertMessage.severity}
            onClose={() => clearAlertMessage(setAlertMessage)}
          />
        </Grid>

        <Loading loading={loading} />
        <FilterContainer>
          <Grid item xs={12} lg={3}>
            <TextField label={'Nome'} name={'name'} value={filters.name} onChange={onChange} />
          </Grid>
          <Grid item xs={12} lg={3}>
            <TextField
              label={'Documento'}
              name={'document'}
              value={filters.document}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <TextField
              select
              label={'Setor de atuação'}
              name={'industry'}
              value={filters.industry}
              onChange={onChange}
            >
              {industries.map((industry) => (
                <MenuItem key={industry.uuid} value={industry.uuid}>
                  {industry.title?.toTitleCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} lg={1}>
            <FilterButton onClick={clearForm} />
          </Grid>
        </FilterContainer>

        <Table>
          <TableResponsive />
        </Table>

        <div className={'buttonGroup twoButtons'}>
          <Button variant="outlined" onClick={() => goToAdmin(navigate)}>
            Voltar
          </Button>
          <span></span>
        </div>
      </HiddenForPermission>
    </BasePage>
  );
}
